import {openDB} from 'idb';
import {EStructureItem} from '@progress-fe/core';

interface IProjectLastItem {
  checkpointId: string;
  entityType: EStructureItem;
  entityOrSubEntityId: string | null;
}

interface IProjectLastCheckpoint {
  projectId: string;
  checkpointId: string;
}

export const projectDbStorage = {
  _initDb: () => {
    return openDB(`prog_projects`, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('lastItems')) {
          db.createObjectStore('lastItems', {keyPath: 'checkpointId', autoIncrement: false});
        }
        if (!db.objectStoreNames.contains('lastCheckpoints')) {
          db.createObjectStore('lastCheckpoints', {keyPath: 'projectId', autoIncrement: false});
        }
      }
    });
  },
  getLastItem: async (checkpointId: string): Promise<IProjectLastItem | null> => {
    const db = await projectDbStorage._initDb();
    return db.get('lastItems', checkpointId);
  },
  getLastCheckpoint: async (projectId: string): Promise<IProjectLastCheckpoint | null> => {
    const db = await projectDbStorage._initDb();
    return db.get('lastCheckpoints', projectId);
  },
  addOrUpdateLastItem: async (item: IProjectLastItem) => {
    try {
      const foundItemState = await projectDbStorage.getLastItem(item.checkpointId);

      const db = await projectDbStorage._initDb();
      const tx = db.transaction('lastItems', 'readwrite');
      const store = tx.objectStore('lastItems');

      if (!foundItemState) {
        await store.add(item);
        console.info(`[Db storage]: Last item added.`);
      } else {
        await store.put(item);
        console.info(`[Db storage]: Last item updated.`);
      }

      await tx.done;
    } catch (e) {
      console.warn(`[Db storage]: addOrUpdateItemState error.`, e);
    }
  },
  addOrUpdateLastCheckpoint: async (checkpoint: IProjectLastCheckpoint) => {
    try {
      const foundCheckpoint = await projectDbStorage.getLastCheckpoint(checkpoint.projectId);

      const db = await projectDbStorage._initDb();
      const tx = db.transaction('lastCheckpoints', 'readwrite');
      const store = tx.objectStore('lastCheckpoints');

      if (!foundCheckpoint) {
        await store.add(checkpoint);
        console.info(`[Db storage]: Last checkpoint added. Id ${checkpoint.checkpointId}`);
      } else {
        await store.put(checkpoint);
        console.info(`[Db storage]: Last checkpoint updated. Id ${checkpoint.checkpointId}`);
      }

      await tx.done;
    } catch (e) {
      console.warn(`[Db storage]: addOrUpdateLastCheckpoint error.`, e);
    }
  },
  deleteLastItems: async (checkpointIds: Array<string>) => {
    try {
      const db = await projectDbStorage._initDb();
      const tx = db.transaction('lastItems', 'readwrite');
      const store = tx.objectStore('lastItems');

      for (const id of checkpointIds) {
        await store.delete(id);
      }

      await tx.done;
    } catch (e) {
      console.warn(`[Db storage]: deleteLastItems error.`, e);
    }
  },
  deleteLastCheckpoint: async (projectId: string) => {
    try {
      const db = await projectDbStorage._initDb();
      const tx = db.transaction('lastCheckpoints', 'readwrite');
      const store = tx.objectStore('lastCheckpoints');

      store.delete(projectId);

      await tx.done;
    } catch (e) {
      console.warn(`[Db storage]: deleteLastCheckpoints error.`, e);
    }
  }
};
